import { WEB_APP_URL } from '@/stores/Constant'
import {
  AppointmentEventType,
  AppointmentHistory,
  DepositType,
  DoctorWorkSchedule,
  ICreateAppointment,
  RecentAppointment,
} from '@/type/Appointment'
import axios from 'axios'
import { useAppStore } from '@/stores/AppStore'
import { Pagination } from '@/type/Global'
import { DensityColorLabel } from '@/composables/Appointment/useAppointmentCalendar'

export function useAppointmentRepository() {
  const appStore = useAppStore()

  async function fetchAppointmentWithDuration(
    user_uuid: string,
    start_date: string,
    end_date: string,
  ): Promise<AppointmentHistory[]> {
    try {
      const { data } = await axios.get(`${WEB_APP_URL}/api/v1/appointment/customer/appointment-history/`, {
        params: {
          start_date: start_date,
          end_date: end_date,
          user_uuid: user_uuid,
        },
      })
      return data.map((item: AppointmentHistory & { branch_line_crm_name?: string }) => ({
        ...item,
        branch_name: item.branch_line_crm_name ?? item.branch_name,
      }))
    } catch (error) {
      throw error
    }
  }

  async function fetchRecentAppointments(limit: number) {
    return axios.get<Pagination<RecentAppointment>>(`${appStore.url}/api/v1/crm/line-app/p/recent-appointments/`, {
      params: { limit, channelId: appStore.channelId, line_user_id: appStore.userProfile.userId },
    })
  }

  async function fetchMoreRecentAppointments(nextPageUrl: string) {
    return axios.get<Pagination<RecentAppointment>>(appStore.url + nextPageUrl)
  }

  async function createAppointment(
    data: ICreateAppointment,
    clinic_uuid: string,
    line_user_id: string,
    channel_id?: string,
  ) {
    try {
      const response = await axios.post(`${appStore.url}/api/v1/crm/line-app/p/appointment/`, data, {
        params: {
          c: clinic_uuid,
          l: line_user_id,
          channelId: channel_id,
        },
      })
      // Return response เพราะในแต่ละ component จะต้อง handle error เอง
      return response
    } catch (error) {
      throw error
    }
  }

  async function cancelAppointment(appointment_uuid: string) {
    return axios.delete(`${appStore.url}/api/v1/crm/line-app/p/cancel-appointment/${appointment_uuid}/`, {
      params: {
        channelId: appStore.channelId,
        line_user_id: appStore.userProfile.userId,
      },
    })
  }

  async function postponeAppointment(params: {
    appointmentUuid: string
    clinicId: string
    lineUserId: string
    channelId: string
    data: ICreateAppointment
  }) {
    return axios.post(`${appStore.url}/api/v1/crm/line-app/p/appointment/`, params.data, {
      params: {
        c: params.clinicId,
        l: params.lineUserId,
        channelId: params.channelId,
        u: params.appointmentUuid,
      },
    })
  }

  async function fetchAppointmentData(params: {
    clinicId: string
    lineUserId: string
    channelId?: string
    appointmentUUID?: string
  }) {
    const url = `${appStore.url}/api/v1/crm/line-app/p/appointment/`
    const { data } = await axios.get(url, {
      params: {
        c: params.clinicId,
        l: params.lineUserId,
        channelId: params.channelId,
        u: params.appointmentUUID,
      },
    })
    return data
  }

  async function fetchBranches(params: { clinicId: string; lineUserId: string; channelId?: string }) {
    const url = `${appStore.url}/api/v1/crm/line-app/p/branch/`
    const { data } = await axios.get(url, {
      params: {
        c: params.clinicId,
        l: params.lineUserId,
        channelId: params.channelId,
      },
    })
    return data
  }

  async function fetchAppointmentSettings(params: { clinicId: string; lineUserId: string; channelId?: string }) {
    const url = `${appStore.url}/api/v1/crm/line-app/p/appointment/setting/`
    const { data } = await axios.get(url, {
      params: {
        c: params.clinicId,
        l: params.lineUserId,
        channelId: params.channelId,
      },
    })
    return data
  }

  async function fetchNoAppointmentDates(params: { branchUuid: string; channelId?: string }) {
    const url = `${appStore.url}/api/v1/crm/line-app/p/no-appointment/`
    const { data } = await axios.get(url, {
      params: { b: params.branchUuid, channelId: params.channelId },
    })
    return data.map((x: any) => ({
      isAllDay: x.is_all_day,
      datetime: new Date(x.date).setHours(0, 0, 0, 0),
      startTime: x.start_time,
      endTime: x.end_time,
    }))
  }

  async function fetchDoctors(params: { clinicId: string; lineUserId: string; branchUuid: string }) {
    const url = `${appStore.url}/api/v1/crm/line-app/p/doctor/`
    const { data } = await axios.get(url, {
      params: { c: params.clinicId, l: params.lineUserId, b: params.branchUuid },
    })
    return data
  }

  async function fetchDoctorWorkSchedule(params: {
    clinicId: string
    lineUserId: string
    branchUuid: string
    doctorUuid: string
  }) {
    const url = `${appStore.url}/api/v1/crm/line-app/p/work-schedule/`
    const { data } = await axios.get(url, {
      params: {
        c: params.clinicId,
        l: params.lineUserId,
        b: params.branchUuid,
        d: params.doctorUuid,
      },
    })
    return data
  }

  function normalizeTimeSlotsResponse(data: unknown) {
    if (Array.isArray(data)) {
      return { working_hour_json_slot: data, density_description: [] as DensityColorLabel[] }
    }
    if (data && typeof data === 'object') {
      const response = data as {
        working_hour_json_slot?: unknown
        density_description?: DensityColorLabel[]
      }
      return {
        working_hour_json_slot: Array.isArray(response.working_hour_json_slot) ? response.working_hour_json_slot : [],
        density_description: response.density_description ?? [],
      }
    }
    return { working_hour_json_slot: [], density_description: [] as DensityColorLabel[] }
  }

  async function fetchTimeSlots(params: {
    clinicId: string
    branchUuid: string
    doctorUuid: string | null
    date: string
  }) {
    const url = `${appStore.url}/api/v1/crm/line-app/p/appointment/use-time/`
    const { data } = await axios.get(url, {
      params: {
        c: params.clinicId,
        b: params.branchUuid,
        d: params.doctorUuid,
        p: params.date,
      },
    })
    return normalizeTimeSlotsResponse(data)
  }

  async function fetchDailyDoctorWork(params: {
    clinicUuid: string
    branchUuid: string
    start: Date
    end: Date
  }): Promise<DoctorWorkSchedule[]> {
    const url = `${appStore.url}/api/v1/crm/line-app/p/doctor-work/`
    const { data } = await axios.get<DoctorWorkSchedule[]>(url, {
      params: {
        clinic_uuid: params.clinicUuid,
        branch_uuid: params.branchUuid,
        start: params.start,
        end: params.end,
      },
    })
    return data
  }

  async function createAppointmentTrickingEvent(eventType: AppointmentEventType) {
    return axios.post(`${appStore.url}/api/v1/crm/line-app/p/appointment/event/`, {
      event_type: eventType,
      channel_id: appStore.channelId,
      line_user_id: appStore.userProfile.userId,
      line_display_name: appStore.userProfile.displayName,
    })
  }

  /** บริการที่สนใจที่ LINE OA เปิดอยู่ พร้อมหัตถการใต้แต่ละบริการ (LMP-7840) - backend เรียงลำดับมาแล้ว */
  async function fetchServices(params: { clinicUuid: string; channelId: string }): Promise<DepositType[]> {
    const { data } = await axios.get<DepositType[]>(`${appStore.url}/api/v1/crm/line-app/p/appointment/service/`, {
      params: {
        c: params.clinicUuid,
        channelId: params.channelId,
      },
    })
    return data
  }

  return {
    fetchAppointmentWithDuration,
    fetchRecentAppointments,
    fetchMoreRecentAppointments,
    createAppointment,
    cancelAppointment,
    postponeAppointment,
    fetchAppointmentData,
    fetchBranches,
    fetchAppointmentSettings,
    fetchNoAppointmentDates,
    fetchDoctors,
    fetchDoctorWorkSchedule,
    fetchTimeSlots,
    fetchDailyDoctorWork,
    createAppointmentTrickingEvent,
    fetchServices,
  }
}
